// React
import React, { useMemo, useState } from 'react';

// components
import { ADGMImage } from 'aurora-components';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Box from '@material-ui/core/Box';

// utils
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { keyEmptyCheck } from 'aurora-utils/utils';

// Styles
import withStyles from '@material-ui/core/styles/withStyles';
import uniqueId from 'lodash/uniqueId';
import styles from './styles';
const MAX_CHAR_TO_SHOW = 130;
const MAX_CONDITIONS_TO_SHOW = 3;

const checkReadMore = (text, length) => {
  if (text?.length > length) {
    return true;
  }
  return false;
};

const ConditionsView = ({ classes, data, isEditable, onEdit, onDelete }) => {
  if (isEmpty(data)) return null;

  const [readMore, setReadMore] = useState({});

  const [shouldNotShowAllData, setshouldNotShowAllData] = useState(data?.length > MAX_CONDITIONS_TO_SHOW);
  const terms = useMemo(() => {
    return (shouldNotShowAllData ? [...data].slice(0, MAX_CONDITIONS_TO_SHOW) : data).map(term => ({
      ...term,
      id: term.id ?? uniqueId('term'),
    }));
  }, [shouldNotShowAllData, data]);

  return (
    <Box display='flex' flexDirection='column' gridGap='16px'>
      {terms?.map((item, index) => {
        const conditionText = `term_index_${item?.id}`;
        const shouldShowReadMore =
          !readMore[conditionText] &&
          (checkReadMore(item?.appendixEn, MAX_CHAR_TO_SHOW) || checkReadMore(item?.appendixAr, MAX_CHAR_TO_SHOW));
        return (
          <Box
            border='1px solid #CED1D6'
            borderRadius='0px'
            p='1em'
            key={conditionText}
            className={classes.itemWrapper}
            mb='1em'
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              borderBottom='1px solid #EBEEF3'
              paddingBottom='16px'
            >
              <Box display='grid' gridGap={8} gridTemplateColumns={`${'0.2fr 1fr'}`} alignItems='center'>
                <Box
                  width='32px'
                  height='32px'
                  display='inline-flex'
                  alignItems='center'
                  justifyContent='center'
                  bgcolor='#EBEEF3'
                  fontWeight='bold'
                  borderRadius='4px'
                >
                  {index + 1}
                </Box>
                <Typography className={classes.itemTitle}>{keyEmptyCheck('term_index', { i: index + 1 })}</Typography>
              </Box>
              {isEditable && (
                <Box display='flex' alignItems='center' className={classes.itemActionsWrapper}>
                  <IconButton
                    onClick={() => onEdit({ ...item, index })}
                    variant='outlined'
                    className={classes.actionBtn}
                  >
                    {/* <ADGMImage name='edit-pen-outline.svg' width='14px' /> */}
                    <span>
                      <svg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <g clipPath='url(#clip0_3783_5026)'>
                          <mask id='path-1-inside-1_3783_5026' fill='white'>
                            <path d='M2.9165 11.0838H5.49134L13.4498 3.12535C13.7907 2.78362 13.9821 2.32063 13.9821 1.83793C13.9821 1.35524 13.7907 0.892251 13.4498 0.550517C13.1031 0.219122 12.6419 0.0341797 12.1623 0.0341797C11.6827 0.0341797 11.2216 0.219122 10.8749 0.550517L2.9165 8.50893V11.0838ZM4.08317 8.99193L11.6998 1.37535C11.8243 1.25632 11.99 1.18991 12.1623 1.18991C12.3346 1.18991 12.5003 1.25632 12.6249 1.37535C12.7474 1.49815 12.8162 1.6645 12.8162 1.83793C12.8162 2.01136 12.7474 2.17772 12.6249 2.30052L5.00834 9.9171H4.08317V8.99193Z' />
                          </mask>
                          <path
                            d='M2.9165 11.0838H5.49134L13.4498 3.12535C13.7907 2.78362 13.9821 2.32063 13.9821 1.83793C13.9821 1.35524 13.7907 0.892251 13.4498 0.550517C13.1031 0.219122 12.6419 0.0341797 12.1623 0.0341797C11.6827 0.0341797 11.2216 0.219122 10.8749 0.550517L2.9165 8.50893V11.0838ZM4.08317 8.99193L11.6998 1.37535C11.8243 1.25632 11.99 1.18991 12.1623 1.18991C12.3346 1.18991 12.5003 1.25632 12.6249 1.37535C12.7474 1.49815 12.8162 1.6645 12.8162 1.83793C12.8162 2.01136 12.7474 2.17772 12.6249 2.30052L5.00834 9.9171H4.08317V8.99193Z'
                            fill='#130F26'
                          />
                          <path
                            d='M2.9165 11.0838H-18.4168V32.4171H2.9165V11.0838ZM5.49134 11.0838V32.4171H14.3279L20.5763 26.1687L5.49134 11.0838ZM13.4498 3.12535L28.5347 18.2103L28.5439 18.2011L28.5532 18.1918L13.4498 3.12535ZM13.9821 1.83793H35.3154H13.9821ZM13.4498 0.550517L28.5532 -14.5159L28.3741 -14.6955L28.1907 -14.8707L13.4498 0.550517ZM10.8749 0.550517L-3.86605 -14.8707L-4.03994 -14.7045L-4.21003 -14.5344L10.8749 0.550517ZM2.9165 8.50893L-12.1684 -6.57601L-18.4168 -0.327622V8.50893H2.9165ZM4.08317 8.99193L-11.0018 -6.09301L-17.2502 0.155377V8.99193H4.08317ZM11.6998 1.37535L-3.0366 -14.0503L-3.21284 -13.8819L-3.38519 -13.7096L11.6998 1.37535ZM12.1623 1.18991V-20.1434V1.18991ZM12.6249 1.37535L27.7304 -13.6891L27.548 -13.8719L27.3613 -14.0503L12.6249 1.37535ZM12.8162 1.83793H-8.51717H12.8162ZM12.6249 2.30052L27.7099 17.3855L27.7201 17.3752L27.7304 17.3649L12.6249 2.30052ZM5.00834 9.9171V31.2504H13.8449L20.0933 25.002L5.00834 9.9171ZM4.08317 9.9171H-17.2502V31.2504H4.08317V9.9171ZM2.9165 32.4171H5.49134V-10.2496H2.9165V32.4171ZM20.5763 26.1687L28.5347 18.2103L-1.63519 -11.9596L-9.59361 -4.00118L20.5763 26.1687ZM28.5532 18.1918C32.8835 13.8508 35.3154 7.96953 35.3154 1.83793H-7.35124C-7.35124 -3.32828 -5.30223 -8.28358 -1.65366 -11.9411L28.5532 18.1918ZM35.3154 1.83793C35.3154 -4.29366 32.8835 -10.1749 28.5532 -14.5159L-1.65366 15.617C-5.30224 11.9594 -7.35124 7.00414 -7.35124 1.83793H35.3154ZM28.1907 -14.8707C23.8744 -18.9966 18.1334 -21.2992 12.1623 -21.2992V21.3675C7.15049 21.3675 2.33168 19.4349 -1.29122 15.9718L28.1907 -14.8707ZM12.1623 -21.2992C6.19131 -21.2992 0.450239 -18.9966 -3.86605 -14.8707L25.6159 15.9718C21.993 19.4349 17.1742 21.3675 12.1623 21.3675V-21.2992ZM-4.21003 -14.5344L-12.1684 -6.57601L18.0014 23.5939L25.9599 15.6355L-4.21003 -14.5344ZM-18.4168 8.50893V11.0838H24.2498V8.50893H-18.4168ZM19.1681 24.0769L26.7847 16.4603L-3.38519 -13.7096L-11.0018 -6.09301L19.1681 24.0769ZM26.4361 16.801C22.5916 20.4738 17.4792 22.5232 12.1623 22.5232V-20.1434C6.50081 -20.1434 1.05714 -17.9611 -3.0366 -14.0503L26.4361 16.801ZM12.1623 22.5232C6.84539 22.5232 1.73309 20.4737 -2.11143 16.801L27.3613 -14.0503C23.2676 -17.9611 17.8239 -20.1434 12.1623 -20.1434V22.5232ZM-2.48053 16.4398C-6.34625 12.5635 -8.51717 7.31246 -8.51717 1.83793H34.1495C34.1495 -3.98345 31.841 -9.56721 27.7304 -13.6891L-2.48053 16.4398ZM-8.51717 1.83793C-8.51717 -3.63659 -6.34625 -8.88764 -2.48053 -12.7639L27.7304 17.3649C31.841 13.2431 34.1495 7.65932 34.1495 1.83793H-8.51717ZM-2.46003 -12.7844L-10.0766 -5.16784L20.0933 25.002L27.7099 17.3855L-2.46003 -12.7844ZM5.00834 -11.4162H4.08317V31.2504H5.00834V-11.4162ZM25.4165 9.9171V8.99193H-17.2502V9.9171H25.4165Z'
                            fill='#130F26'
                            mask='url(#path-1-inside-1_3783_5026)'
                          />
                          <path
                            d='M13.7789 4.44617L12.8333 5.39175V9.33333H9.33333V12.8333H1.16667V1.75C1.16667 1.59529 1.22812 1.44692 1.33752 1.33752C1.44692 1.22812 1.59529 1.16667 1.75 1.16667H8.60883L9.55442 0.221083C9.63958 0.142114 9.72938 0.0682886 9.82333 0L1.75 0C1.28587 0 0.840752 0.184374 0.512563 0.512563C0.184374 0.840752 0 1.28587 0 1.75L0 14H10.7415L14 10.7415V4.17725C13.9316 4.27109 13.8577 4.36088 13.7789 4.44617ZM10.5 12.5918V10.5H12.5918L10.5 12.5918Z'
                            fill='#130F26'
                          />
                        </g>
                        <defs>
                          <clipPath id='clip0_3783_5026'>
                            <rect width='14' height='14' fill='white' />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                  </IconButton>
                  <IconButton
                    onClick={() => onDelete({ ...item, index })}
                    variant='outlined'
                    className={classes.actionBtn}
                  >
                    {/* <ADGMImage name='DeleteImg.svg' width='14px' />
                     */}
                    <span>
                      <svg width='17' height='17' viewBox='0 0 17 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <g clipPath='url(#clip0_2253_58763)'>
                          <path
                            d='M15.1673 3.16667H11.834V1.83333C11.834 1.47971 11.6935 1.14057 11.4435 0.890524C11.1934 0.640476 10.8543 0.5 10.5007 0.5H6.50065C6.14703 0.5 5.80789 0.640476 5.55784 0.890524C5.30779 1.14057 5.16732 1.47971 5.16732 1.83333V3.16667H1.83398V4.5H3.16732V14.5C3.16732 15.0304 3.37803 15.5391 3.7531 15.9142C4.12818 16.2893 4.63688 16.5 5.16732 16.5H11.834C12.3644 16.5 12.8731 16.2893 13.2482 15.9142C13.6233 15.5391 13.834 15.0304 13.834 14.5V4.5H15.1673V3.16667ZM6.50065 1.83333H10.5007V3.16667H6.50065V1.83333ZM12.5007 14.5C12.5007 14.6768 12.4304 14.8464 12.3054 14.9714C12.1804 15.0964 12.0108 15.1667 11.834 15.1667H5.16732C4.99051 15.1667 4.82094 15.0964 4.69591 14.9714C4.57089 14.8464 4.50065 14.6768 4.50065 14.5V4.5H12.5007V14.5Z'
                            fill='black'
                          />
                          <path d='M7.83333 7.16675H6.5V12.5001H7.83333V7.16675Z' fill='black' />
                          <path d='M10.5013 7.16675H9.16797V12.5001H10.5013V7.16675Z' fill='black' />
                        </g>
                        <defs>
                          <clipPath id='clip0_2253_58763'>
                            <rect width='16' height='16' fill='white' transform='translate(0.5 0.5)' />
                          </clipPath>
                        </defs>
                      </svg>
                    </span>
                  </IconButton>
                </Box>
              )}
            </Box>
            <Grid container className={classes.itemContentWrapper}>
              <Grid item xs={6} md={6} className={classes.itemENContent}>
                {shouldShowReadMore ? `${item?.appendixEn?.slice(0, MAX_CHAR_TO_SHOW)}...` : item?.appendixEn}
              </Grid>
              <Grid item xs={6} md={6} className={classes.itemArContent}>
                {shouldShowReadMore ? `${item?.appendixAr?.slice(0, 140)}...` : item?.appendixAr}
              </Grid>
            </Grid>
            {(typeof readMore[conditionText] === 'boolean' || shouldShowReadMore) && (
              <Box>
                <Button
                  variant='text'
                  className={classes.readMoreLink}
                  onClick={() => {
                    setReadMore({
                      ...readMore,
                      [conditionText]: !readMore[conditionText],
                    });
                  }}
                >
                  <span>{shouldShowReadMore ? keyEmptyCheck('read_more') : keyEmptyCheck('show_less')}</span>
                  <ADGMImage
                    name={shouldShowReadMore ? 'arrow-down-brown.svg' : 'arrow-up-brown.svg'}
                    alt='read_more'
                    className={classes.toggleIcon}
                  />
                </Button>
              </Box>
            )}
          </Box>
        );
      })}
      {data?.length > MAX_CONDITIONS_TO_SHOW && (
        <Box mb='1em' textAlign='center'>
          <Button
            variant='text'
            className={classes.readMoreLink}
            onClick={() => {
              setshouldNotShowAllData(!shouldNotShowAllData);
            }}
          >
            <span>{shouldNotShowAllData ? keyEmptyCheck('see_all_terms') : keyEmptyCheck('see_less_terms')}</span>
            <ADGMImage
              name={shouldNotShowAllData ? 'arrow-down-brown.svg' : 'arrow-up-brown.svg'}
              alt='read_more'
              className={classes.toggleIcon}
            />
          </Button>
        </Box>
      )}
    </Box>
  );
};
ConditionsView.propTypes = {
  classes: PropTypes.object.isRequired,
  data: PropTypes.array.isRequired,
  isEditable: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default withStyles(styles)(ConditionsView);
